/**
 * TermsAcceptanceModal — full-screen click-to-accept modal that gates the
 * app for logged-in users whose ToS acceptance is out-of-date (i.e. their
 * `terms_version_accepted` doesn't match the current server version).
 *
 * The user must tick BOTH checkboxes:
 *   (a) Anti-piracy prosecution clause
 *   (b) Canadian jurisdiction clause
 *
 * On accept, hits `POST /api/legal/accept-terms` and closes.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { ShieldCheck, Scale } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const TermsAcceptanceModal = ({ open, currentVersion, onAccepted }) => {
  const [prosecutionAck, setProsecutionAck] = useState(false);
  const [jurisdictionAck, setJurisdictionAck] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const canSubmit = prosecutionAck && jurisdictionAck && !submitting;

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      const token = window.localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/legal/accept-terms`,
        {
          version: currentVersion,
          accepted_prosecution_clause: true,
          accepted_canadian_jurisdiction: true,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Terms accepted — thanks!');
      onAccepted?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record acceptance.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="terms-acceptance-modal"
    >
      <div className="bg-background rounded-2xl border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 md:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
              <Scale className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">
                Updated Terms of Use
              </div>
              <h2 className="font-heading text-2xl font-black text-foreground leading-tight">
                Two quick things before you continue
              </h2>
              <p className="text-xs text-muted-foreground mt-1">Version {currentVersion}</p>
            </div>
          </div>

          <p className="text-sm text-foreground/85 leading-relaxed mb-5">
            We&rsquo;ve updated the Freeze Dry Academy Terms of Use to strengthen
            protection of our courses, community, and members. Please review
            the highlights below and confirm — you can read the full policy{' '}
            <Link to="/terms" className="underline font-bold" target="_blank" rel="noopener">here</Link>.
          </p>

          <div className="space-y-3">
            <label
              className="flex items-start gap-3 p-4 rounded-xl border border-border cursor-pointer hover:bg-muted/40 transition-colors"
              data-testid="terms-prosecution-label"
            >
              <input
                type="checkbox"
                checked={prosecutionAck}
                onChange={(e) => setProsecutionAck(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-input"
                data-testid="terms-prosecution-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-primary" />
                  <span className="text-sm font-heading font-extrabold text-foreground">
                    I will not copy or redistribute course content
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  I understand all lessons, videos, PDFs, quizzes, and written
                  materials are the copyrighted property of Peak and Prairie
                  Industries / Freeze Dry Academy. If I copy, scrape,
                  screenshot for redistribution, republish, or share content
                  without written permission, I may be pursued to the full
                  extent of civil and criminal law, including statutory
                  damages, injunctions, and legal-cost recovery.
                </p>
              </div>
            </label>

            <label
              className="flex items-start gap-3 p-4 rounded-xl border border-border cursor-pointer hover:bg-muted/40 transition-colors"
              data-testid="terms-jurisdiction-label"
            >
              <input
                type="checkbox"
                checked={jurisdictionAck}
                onChange={(e) => setJurisdictionAck(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-input"
                data-testid="terms-jurisdiction-checkbox"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-primary" />
                  <span className="text-sm font-heading font-extrabold text-foreground">
                    Canadian jurisdiction &amp; exclusive venue
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  I agree that these Terms are governed by the laws of the
                  Province of Alberta and the federal laws of Canada
                  applicable therein. I irrevocably submit to the exclusive
                  jurisdiction of the courts of Alberta, Canada for any
                  dispute arising from my use of Freeze Dry Academy — even
                  if I access the platform from outside Canada. I waive any
                  objection based on inconvenient forum.
                </p>
              </div>
            </label>
          </div>

          <div className="mt-6 flex flex-col-reverse sm:flex-row items-center gap-3 justify-end">
            <Link
              to="/terms"
              target="_blank"
              rel="noopener"
              className="text-xs text-muted-foreground underline hover:text-foreground"
            >
              Read full Terms of Use
            </Link>
            <Button
              onClick={handleAccept}
              disabled={!canSubmit}
              className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-bold"
              data-testid="terms-accept-btn"
            >
              {submitting ? 'Recording…' : 'I accept both clauses'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
